import React, { useState } from 'react';
import type { Demand } from '../types';
import { demandsAPI } from '../api';
import { Search, Download, ArrowUpRight, Star, Edit3, ArrowUpDown, Trash2, Filter, Smartphone } from 'lucide-react';
import { MobileDemandCard } from './mobile/MobileDemandCard';
import { FilterDrawer } from './mobile/FilterDrawer';
import { useAlert } from '../contexts/AlertContext';

interface DemandListProps {
  demands: Demand[];
  globalSearch: string;
  onSelectDemand: (id: string) => void;
  onSelectDemandEdit: (id: string) => void;
  userRole: string;
  userPermissions: string[];
}

export const DemandList: React.FC<DemandListProps> = ({ 
  demands, 
  globalSearch, 
  onSelectDemand, 
  onSelectDemandEdit, 
  userRole,
  userPermissions
}) => {
  const { showAlert } = useAlert();
  const [searchTerm, setSearchTerm] = useState('');
  const [cabinetFilter, setCabinetFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [filterFavorite, setFilterFavorite] = useState(false);
  
  // Mobile filter drawer
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  
  // Ordenação: 'date_desc' | 'priority_desc'
  const [sortBy, setSortBy] = useState<'date_desc' | 'priority_desc'>('date_desc');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;

  // Estado para exclusão lógica
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [demandToDelete, setDemandToDelete] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Integrar com pesquisa global ou local
  const activeSearchTerm = globalSearch || searchTerm;

  const handleToggleFavorite = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await demandsAPI.toggleFavorite(id);
      const demand = demands.find(d => d.id === id);
      if (demand) demand.isFavorite = !demand.isFavorite; 
      setSearchTerm(prev => prev + ' ');
      setTimeout(() => setSearchTerm(prev => prev.trim()), 10);
    } catch (err) {
      showAlert('error', 'Erro', 'Erro ao favoritar demanda.');
    }
  };

  const openDeleteModal = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDemandToDelete(id);
    setDeleteReason('');
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!demandToDelete || !deleteReason.trim()) {
      showAlert('info', 'Aviso', 'Informe um motivo para a exclusão.');
      return;
    }
    setIsDeleting(true);
    try {
      await demandsAPI.delete(demandToDelete, deleteReason);
      showAlert('success', 'Sucesso', 'Demanda excluída com sucesso.');
      // Atualiza estado local para ocultar
      const d = demands.find(d => d.id === demandToDelete);
      if (d) d.status = 'EXCLUIDA';
      setIsDeleteModalOpen(false);
    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao excluir a demanda.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeSearchTerm, cabinetFilter, statusFilter, areaFilter, cityFilter, filterFavorite, sortBy]);

  // Filtrar demandas no frontend
  let filteredDemands = demands.filter(d => {
    // Restrição de Role
    if (userRole === 'STATE_CABINET' && d.cabinet === 'FEDERAL') return false;
    if (userRole === 'FEDERAL_CABINET' && d.cabinet === 'STATE') return false;

    // Filtro de Favoritos
    if (filterFavorite && !d.isFavorite) return false;

    // Esconder EXCLUIDA por padrão a menos que explicitamente buscado
    if (d.status === 'EXCLUIDA' && statusFilter !== 'EXCLUIDA') return false;

    // Esconder RECEBIDA (aguardando triagem) por padrão a menos que explicitamente buscado
    if (d.status === 'RECEBIDA' && statusFilter !== 'RECEBIDA') return false;

    // Filtros de UI
    const matchSearch =
      activeSearchTerm === '' ||
      d.protocol.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
      d.title.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
      d.solicitanteName.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
      d.city.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
      d.themeArea.toLowerCase().includes(activeSearchTerm.toLowerCase());

    const matchCabinet = cabinetFilter === '' || d.cabinet === cabinetFilter || d.cabinet === 'BOTH';
    const matchStatus = statusFilter === '' || d.status === statusFilter;
    const matchArea = areaFilter === '' || d.themeArea === areaFilter;
    const matchCity = cityFilter === '' || d.city.toLowerCase() === cityFilter.toLowerCase();

    return matchSearch && matchCabinet && matchStatus && matchArea && matchCity;
  });

  // Ordenar Demandas
  filteredDemands = [...filteredDemands].sort((a, b) => {
    if (sortBy === 'priority_desc') {
      const priorityWeights = { 'URGENTE': 4, 'ALTA': 3, 'MEDIA': 2, 'BAIXA': 1 };
      const weightA = priorityWeights[a.priority as keyof typeof priorityWeights] || 1;
      const weightB = priorityWeights[b.priority as keyof typeof priorityWeights] || 1;
      return weightB - weightA;
    }
    // Default: date desc
    return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
  });

  const totalPages = Math.ceil(filteredDemands.length / ITEMS_PER_PAGE);
  const paginatedDemands = filteredDemands.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleExportCSV = () => {
    const headers = ['Protocolo', 'Data Recebimento', 'Gabinete', 'Tipo', 'Area', 'Cidade', 'Solicitante', 'Enviado Por', 'Valor Solicitado', 'Prioridade', 'Status', 'Confiança'];
    const rows = filteredDemands.map(d => [
      d.protocol,
      new Date(d.receivedAt).toLocaleDateString('pt-BR'),
      d.cabinet === 'STATE' ? 'Dani Alonso' : (d.cabinet === 'FEDERAL' ? 'Cap. Augusto' : 'Ambos'),
      d.type,
      d.themeArea,
      d.city,
      d.solicitanteName,
      d.senderName || '-',
      d.valueRequested || 0,
      d.priority,
      d.status,
      `${d.confidenceScore}%`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `demandas_gabinete_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusStyle = (status: string) => {
    const styles: Record<string, string> = {
      'RECEBIDA': 'bg-blue-100 text-blue-805 dark:bg-blue-950/40 dark:text-blue-400 border-blue-200/30',
      'EM_TRIAGEM': 'bg-amber-100 text-amber-805 dark:bg-amber-950/40 dark:text-amber-400 border-amber-200/30',
      'EM_ANALISE': 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-400 border-purple-200/30',
      'APROVADA': 'bg-teal-100 text-teal-800 dark:bg-teal-950/40 dark:text-teal-400 border-teal-200/30',
      'CONCLUIDA': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200/30',
      'ARQUIVADA': 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-400 border-slate-800/40',
      'EXCLUIDA': 'bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500 border-slate-300 line-through',
    };
    return styles[status] || 'bg-slate-100 text-slate-800 dark:bg-slate-900';
  };

  const getPriorityStyle = (priority: string) => {
    const styles: Record<string, string> = {
      'BAIXA': 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-400 border-slate-200/50',
      'MEDIA': 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400 border-emerald-250/20',
      'ALTA': 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 border-amber-250/20',
      'URGENTE': 'bg-red-50 text-red-750 dark:bg-red-950/30 dark:text-red-400 border-red-250/20 animate-pulse font-extrabold',
    };
    return styles[priority] || 'bg-slate-100 text-slate-700';
  };

  const uniqueAreas = Array.from(new Set(demands.map(d => d.themeArea)));
  const uniqueCities = Array.from(new Set(demands.map(d => d.city)));

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-sans">Gestão de Demandas</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Pesquisa, filtros e exportações de relatórios consolidados do gabinete.
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Ordenador de Prioridade */}
          <button
            onClick={() => setSortBy(sortBy === 'date_desc' ? 'priority_desc' : 'date_desc')}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-xs font-semibold rounded-xl transition-all"
            title="Ordenar Lista"
          >
            <ArrowUpDown className="w-3.5 h-3.5 text-blue-500" />
            Ordenar: {sortBy === 'date_desc' ? 'Data' : 'Prioridade'}
          </button>

          {/* Filtro Favoritas */}
          <button
            onClick={() => setFilterFavorite(!filterFavorite)}
            className={`flex items-center gap-1.5 px-3 py-2 border rounded-xl text-xs font-semibold transition-all ${
              filterFavorite 
                ? 'bg-amber-550/10 border-amber-500 text-amber-600' 
                : 'bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500'
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${filterFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
            Favoritas
          </button>

          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-550 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/10 focus:outline-none"
          >
            <Download className="w-4 h-4" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* ── MOBILE: Filter Bar & Drawer ── */}
      <div className="md:hidden flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="search"
            placeholder="Buscar demandas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none"
          />
        </div>
        <button
          onClick={() => setIsFilterDrawerOpen(true)}
          className="relative px-3 py-2.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-500 font-semibold"
        >
          <Filter className="w-5 h-5" />
          {/* Active filters count badge */}
          {Object.values({ statusFilter, cabinetFilter, areaFilter }).filter(Boolean).length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-blue-500 text-white text-[9px] font-bold flex items-center justify-center rounded-full border-2 border-white dark:border-slate-950">
              {Object.values({ statusFilter, cabinetFilter, areaFilter }).filter(Boolean).length}
            </span>
          )}
        </button>
      </div>

      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={{ status: statusFilter, cabinet: cabinetFilter, priority: sortBy === 'priority_desc' ? 'URGENTE' : '', source: '', themeArea: areaFilter }}
        onApply={(f) => {
          setStatusFilter(f.status || '');
          setCabinetFilter(f.cabinet || '');
          if (f.priority === 'URGENTE') setSortBy('priority_desc'); else setSortBy('date_desc');
          setAreaFilter(f.themeArea || '');
        }}
        onClear={() => {
          setStatusFilter(''); setCabinetFilter(''); setAreaFilter(''); setSortBy('date_desc');
        }}
      />

      {/* ── DESKTOP: Filtros ── */}
      <div className="hidden md:grid glass-panel p-5 rounded-2xl grid-cols-2 lg:grid-cols-5 gap-3 text-left">
        {/* Busca Rápida */}
        <div className="relative">
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 font-mono">Busca Rápida</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Protocolo, título, solicitante..."
              className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl py-2.5 pl-9 pr-3 text-xs focus:outline-none focus:border-blue-500 transition-all font-medium"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Gabinete */}
        {userRole === 'ADMIN' || userRole === 'SHARED_ASSESSOR' || userRole === 'ROLE_ADMIN' || userRole === 'ROLE_CHEFE_GABINETE' || userRole === 'ROLE_ASSESSOR' ? (
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 font-mono">Gabinete</label>
            <select
              className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-blue-550 font-semibold"
              value={cabinetFilter}
              onChange={(e) => setCabinetFilter(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="STATE">Dani Alonso (Estadual)</option>
              <option value="FEDERAL">Capitão Augusto (Federal)</option>
              <option value="BOTH">Ambos</option>
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 font-mono">Gabinete</label>
            <input
              type="text"
              disabled
              className="w-full bg-slate-200 dark:bg-slate-800 border border-slate-200 dark:border-slate-850 rounded-xl py-2.5 px-3 text-xs text-slate-500 cursor-not-allowed font-semibold"
              value={userRole === 'STATE_CABINET' ? 'Dani Alonso (Estadual)' : 'Capitão Augusto (Federal)'}
            />
          </div>
        )}

        {/* Status */}
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 font-mono">Status</label>
          <select
            className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-blue-500 font-semibold"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Todos (Exceto Triagem e Lixeira)</option>
            <option value="RECEBIDA">Recebida</option>
            <option value="EM_TRIAGEM">Em Triagem</option>
            <option value="EM_ANALISE">Em Análise</option>
            <option value="APROVADA">Aprovada</option>
            <option value="CONCLUIDA">Concluída</option>
            <option value="ARQUIVADA">Arquivada</option>
            <option value="EXCLUIDA">Excluída (Lixeira)</option>
          </select>
        </div>

        {/* Area */}
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 font-mono">Área Temática</label>
          <select
            className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-blue-500 font-semibold"
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
          >
            <option value="">Todas</option>
            {uniqueAreas.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>

        {/* Cidade */}
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1 font-mono">Cidade</label>
          <select
            className="w-full bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl py-2.5 px-3 text-xs focus:outline-none focus:border-blue-550 font-semibold"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
          >
            <option value="">Todas</option>
            {uniqueCities.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabela de Resultados (Desktop) */}
      <div className="hidden lg:block glass-panel rounded-2xl overflow-hidden border border-slate-200/50 dark:border-slate-800/80 shadow-md">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800 font-bold text-slate-450 uppercase tracking-wider">
                <th className="py-4 px-5 w-12 text-center">⭐</th>
                <th className="py-4 px-3">Protocolo</th>
                <th className="py-4 px-3">Gabinete</th>
                <th className="py-4 px-3">Título/Assunto</th>
                <th className="py-4 px-3">Cidade</th>
                <th className="py-4 px-3">Remetente (WhatsApp)</th>
                <th className="py-4 px-3">Prioridade</th>
                <th className="py-4 px-3">Status</th>
                <th className="py-4 px-5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
              {paginatedDemands.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-slate-500 font-sans text-sm">
                    Nenhuma demanda correspondente aos filtros foi localizada.
                  </td>
                </tr>
              ) : (
                paginatedDemands.map(d => (
                  <tr key={d.id} className="hover:bg-slate-500/5 transition-all">
                    <td className="py-4 px-5 text-center">
                      <button 
                        onClick={(e) => handleToggleFavorite(d.id, e)}
                        className="text-slate-350 hover:text-amber-500 transition-all focus:outline-none"
                      >
                        <Star className={`w-4 h-4 ${d.isFavorite ? 'fill-amber-500 text-amber-500' : ''}`} />
                      </button>
                    </td>
                    <td className="py-4 px-3 font-mono text-slate-400 font-bold">{d.protocol}</td>
                    <td className="py-4 px-3 font-semibold">
                      {d.cabinet === 'STATE' ? (
                        <span className="text-blue-600 dark:text-blue-400">Dani Alonso</span>
                      ) : (d.cabinet === 'FEDERAL' ? (
                        <span className="text-red-600 dark:text-red-400">Cap. Augusto</span>
                      ) : (
                        <span className="text-amber-600">Ambos</span>
                      ))}
                    </td>
                    <td className="py-4 px-3 font-semibold text-slate-800 dark:text-slate-200 max-w-[200px] truncate">
                      {d.title}
                      <div className="text-[10px] text-slate-500 font-normal truncate mt-0.5">{d.orgaoEmissor || d.solicitanteName}</div>
                    </td>
                    <td className="py-4 px-3 text-slate-650 dark:text-slate-400 font-medium">{d.city}</td>
                    <td className="py-4 px-3">
                      {d.senderPhone ? (
                        <div className="flex flex-col">
                          <span className="text-slate-700 dark:text-slate-300 font-medium flex items-center gap-1"><Smartphone className="w-3 h-3 text-emerald-500"/>{d.senderName || 'Não identif.'}</span>
                          <span className="text-[10px] text-slate-500">{d.senderPhone}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="py-4 px-3">
                      <span className={`px-2 py-0.5 rounded font-bold border text-[9px] ${getPriorityStyle(d.priority)}`}>
                        {d.priority}
                      </span>
                    </td>
                    <td className="py-4 px-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${getStatusStyle(d.status)}`}>
                        {d.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4 px-5 text-right flex items-center justify-end gap-2">
                      <button
                        onClick={() => onSelectDemand(d.id)}
                        className="inline-flex items-center gap-0.5 text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-all font-semibold"
                        title="Ver Detalhes"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </button>

                      {userPermissions.includes('demand:edit') && (
                        <>
                          <button
                            onClick={() => onSelectDemandEdit(d.id)}
                            className="inline-flex items-center gap-0.5 text-slate-450 hover:text-slate-700 dark:hover:text-slate-200 hover:underline transition-all font-semibold"
                            title="Editar Demanda"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => openDeleteModal(d.id, e)}
                            className="inline-flex items-center gap-0.5 text-red-400 hover:text-red-600 hover:underline transition-all font-semibold ml-1"
                            title="Excluir Demanda"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lista de Cards Responsiva (Mobile) */}
      <div className="md:hidden space-y-3">
        {filteredDemands.length === 0 ? (
          <div className="glass-panel p-6 text-center text-slate-500 font-sans text-sm rounded-2xl">
            Nenhuma demanda localizada.
          </div>
        ) : (
          paginatedDemands.map(d => (
            <MobileDemandCard
              key={d.id}
              demand={d}
              onView={onSelectDemand}
              onEdit={onSelectDemandEdit}
              canEdit={userPermissions.includes('demand:edit')}
            />
          ))
        )}
      </div>

      {/* Paginação Geral (Desktop & Mobile) */}
      {filteredDemands.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-[#1a1c23] border border-slate-200/60 dark:border-slate-800/60 rounded-2xl sm:px-6 shadow-sm">
          {/* Desktop Pagination */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Mostrando <span className="font-medium text-blue-600 dark:text-blue-400">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> a <span className="font-medium text-blue-600 dark:text-blue-400">{Math.min(currentPage * ITEMS_PER_PAGE, filteredDemands.length)}</span> de <span className="font-medium text-blue-600 dark:text-blue-400">{filteredDemands.length}</span> resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#1f222b] text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Anterior</span>
                  &larr;
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#1f222b] text-sm font-medium text-slate-700 dark:text-slate-300">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#1f222b] text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Próximo</span>
                  &rarr;
                </button>
              </nav>
            </div>
          </div>

          {/* Mobile pagination */}
          <div className="flex-1 flex justify-between sm:hidden items-center">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-slate-350 dark:border-slate-700 text-xs font-semibold rounded-xl bg-white dark:bg-[#1f222b] text-slate-700 dark:text-slate-300 disabled:opacity-40"
            >
              Anterior
            </button>
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Pág. {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border border-slate-350 dark:border-slate-700 text-xs font-semibold rounded-xl bg-white dark:bg-[#1f222b] text-slate-700 dark:text-slate-300 disabled:opacity-40"
            >
              Próximo
            </button>
          </div>
        </div>
      )}

      {/* Modal de Exclusão */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 w-full max-w-md animate-in fade-in zoom-in duration-200">
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Confirmar Exclusão
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Você está prestes a excluir esta demanda do painel (exclusão lógica). Esta ação não apaga o histórico de mensagens, mas oculta a demanda das listas ativas.
            </p>
            <div className="mb-5">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Motivo da Exclusão:</label>
              <select 
                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl py-2 px-3 text-sm focus:outline-none focus:border-red-400"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
              >
                <option value="">-- Selecione o motivo --</option>
                <option value="Demanda Duplicada">Demanda Duplicada</option>
                <option value="Documento enviado incorretamente">Documento enviado incorretamente</option>
                <option value="Teste do sistema">Teste do sistema</option>
                <option value="Cadastro indevido">Cadastro indevido</option>
                <option value="Outros">Outros</option>
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button 
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl flex items-center gap-2"
                disabled={isDeleting || !deleteReason}
              >
                {isDeleting ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemandList;
